import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { addPeriod, api, aud, fmtDate, isoDate } from './utils';

const OPTIONS = [
  { id: '100', label: '$100 / week' },
  { id: '200', label: '$200 / week' },
  { id: '300', label: '$300 / week' },
  { id: 'four', label: '4 equal payments' },
  { id: 'custom', label: 'Custom' },
];

const initial = (mode, source) => {
  if (mode === 'edit') {
    return { option: source.instalment_option || 'custom', amount: String(source.instalment_amount), frequency: source.frequency,
      interval_count: String(source.interval_count || 21), interval_unit: source.interval_unit || 'days', deposit: String(source.deposit_required ?? 500),
      start: source.start_date, original: String(source.original_balance ?? ''), setup: source.halaxy_plan_setup, special: source.special_arrangements || '' };
  }
  return { option: '100', amount: '100', frequency: 'weekly', interval_count: '21', interval_unit: 'days', deposit: '500', start: isoDate(new Date()),
    original: source ? String(source.balance) : '', setup: false, special: '' };
};

const InvoiceSummary = ({ inv }) => (
  <div className="rounded-xl bg-[#F5F1E8] p-3 text-sm" data-testid="plan-dialog-invoice-summary">
    <p className="font-semibold text-[#3A3530]">{inv.client_name} <span className="font-normal text-[#9B9590]">· #{inv.invoice_id} · {fmtDate(inv.invoice_date)}</span></p>
    <p className="mt-1 text-[#6B6560]">Invoiced {aud(inv.invoiced)} · Paid {aud(inv.paid)} · <b className="text-[#3A3530]">Owing {aud(inv.balance)}</b></p>
  </div>
);

const ManualLookup = ({ onFound }) => {
  const [num, setNum] = useState('');
  const [busy, setBusy] = useState(false);
  const find = async () => {
    setBusy(true);
    try {
      const inv = await api(`/lookup-invoice/${num.trim()}`);
      if (inv.already_on_plan) toast.error('This invoice already has a payment plan');
      else onFound(inv);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-1.5">
      <Label>Halaxy invoice number</Label>
      <div className="flex gap-2">
        <Input value={num} onChange={(e) => setNum(e.target.value.replace(/\D/g, ''))} placeholder="e.g. 873300101" data-testid="plan-dialog-invoice-input" />
        <Button type="button" onClick={find} disabled={num.length < 3 || busy} variant="outline" data-testid="plan-dialog-lookup-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}<span className="ml-1.5">Find</span>
        </Button>
      </div>
      <p className="text-xs text-[#9B9590]">Read-only lookup in Halaxy. Nothing is changed there.</p>
    </div>
  );
};

const schedulePreview = (f) => {
  const amt = parseFloat(f.amount); const total = parseFloat(f.original);
  if (!amt || !total || !f.start) return null;
  const n = Math.ceil(total / amt - 0.0001);
  const d = addPeriod(new Date(`${f.start}T00:00:00`), f, n - 1);
  return `${n} payment${n === 1 ? '' : 's'} · expected payoff around ${fmtDate(isoDate(d))}`;
};

export const PlanDialog = ({ open, mode, source, onClose, onSaved }) => {
  const [inv, setInv] = useState(null);
  const [f, setF] = useState(initial('create'));
  const [saving, setSaving] = useState(false);
  const amountRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setInv(mode === 'suggestion' ? source : null);
    setF(initial(mode, source));
  }, [open, mode, source]);

  const set = (patch) => setF((prev) => {
    const next = { ...prev, ...patch };
    if (patch.option && ['100', '200', '300'].includes(patch.option)) Object.assign(next, { amount: patch.option, frequency: 'weekly' });
    if (patch.option === 'custom' && prev.option !== 'custom' && patch.amount === undefined) next.amount = '';
    if (next.option === 'four' && (patch.option === 'four' || patch.original !== undefined) && patch.amount === undefined) {
      next.amount = next.original ? (Math.round((parseFloat(next.original) / 4) * 100) / 100).toString() : '';
      if (patch.option) next.frequency = 'fortnightly';
    }
    return next;
  });

  const pickOption = (id) => {
    set({ option: id });
    if (id === 'custom') setTimeout(() => amountRef.current?.focus(), 0);
  };

  const onFound = (found) => { setInv(found); setF((p) => ({ ...p, original: String(found.balance) })); };

  const save = async () => {
    setSaving(true);
    const payload = { instalment_amount: parseFloat(f.amount), instalment_option: f.option, frequency: f.frequency, start_date: f.start,
      interval_count: f.frequency === 'other' ? parseInt(f.interval_count, 10) : null, interval_unit: f.frequency === 'other' ? f.interval_unit : null,
      deposit_required: f.deposit === '' ? 0 : parseFloat(f.deposit),
      original_balance: f.original === '' ? null : parseFloat(f.original), halaxy_plan_setup: f.setup, special_arrangements: f.special || null };
    try {
      const saved = mode === 'edit'
        ? await api(`/${source.id}`, { method: 'PUT', body: payload })
        : await api('', { method: 'POST', body: { ...payload, invoice_id: inv.invoice_id } });
      toast.success(mode === 'edit' ? 'Plan updated' : 'Payment plan added');
      onSaved(saved, mode);
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const canSave = (mode === 'edit' || inv) && parseFloat(f.amount) > 0 && f.start && (f.frequency !== 'other' || parseInt(f.interval_count, 10) >= 1);
  const title = mode === 'edit' ? 'Edit payment plan' : mode === 'suggestion' ? 'Confirm payment plan' : 'Add payment plan';

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="plan-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{title}</DialogTitle>
          <DialogDescription>Record what you agreed with the client. Amounts paid and owing always come from Halaxy.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {mode === 'manual' && !inv && <ManualLookup onFound={onFound} />}
          {inv && <InvoiceSummary inv={inv} />}
          {mode === 'edit' && <InvoiceSummary inv={{ ...source, invoice_date: null }} />}
          <div className="space-y-1.5">
            <Label>Payment option</Label>
            <div className="flex flex-wrap gap-2">
              {OPTIONS.map((o) => (
                <button key={o.id} type="button" onClick={() => pickOption(o.id)} data-testid={`plan-option-${o.id}`}
                  className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors duration-150 ${f.option === o.id ? 'border-[#3A3530] bg-[#3A3530] text-white' : 'border-[#E8E4DC] text-[#6B6560] hover:border-[#8B9D83]'}`}>
                  {o.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Original balance owing</Label>
              <Input type="number" min="0" step="0.01" value={f.original} onChange={(e) => set({ original: e.target.value })} data-testid="plan-dialog-original-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{f.option === 'custom' ? 'Custom amount ($)' : 'Instalment amount'}</Label>
              <Input ref={amountRef} type="number" min="1" step="0.01" value={f.amount} placeholder="Enter any amount"
                onChange={(e) => set({ amount: e.target.value, option: f.option === 'four' ? 'four' : 'custom' })}
                className={f.option === 'custom' ? 'border-[#8B9D83] ring-1 ring-[#8B9D83]/40' : ''} data-testid="plan-dialog-amount-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Frequency</Label>
              <Select value={f.frequency} onValueChange={(v) => set({ frequency: v })}>
                <SelectTrigger data-testid="plan-dialog-frequency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="fortnightly">Fortnightly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="other">Other (every X days / months)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>First payment due</Label>
              <Input type="date" value={f.start} onChange={(e) => set({ start: e.target.value })} data-testid="plan-dialog-start-input" />
            </div>
            {f.frequency === 'other' && (
              <div className="col-span-2 space-y-1.5" data-testid="plan-dialog-interval">
                <Label>Repeats every</Label>
                <div className="flex gap-2">
                  <Input type="number" min="1" max="365" step="1" value={f.interval_count} onChange={(e) => set({ interval_count: e.target.value })}
                    className="w-24" data-testid="plan-dialog-interval-count-input" />
                  <Select value={f.interval_unit} onValueChange={(v) => set({ interval_unit: v })}>
                    <SelectTrigger className="w-36" data-testid="plan-dialog-interval-unit-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="days">days</SelectItem>
                      <SelectItem value="months">months</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <div className="col-span-2 space-y-1.5">
              <Label>Deposit required</Label>
              <Input type="number" min="0" step="0.01" value={f.deposit} onChange={(e) => set({ deposit: e.target.value })} data-testid="plan-dialog-deposit-input" />
              <p className="text-xs text-[#9B9590]">Counts as met when Halaxy shows this much paid before the first payment date.</p>
            </div>
          </div>
          {schedulePreview(f) && <p className="text-xs text-[#6B7D63]" data-testid="plan-dialog-preview">{schedulePreview(f)}</p>}
          <div className="flex items-center justify-between rounded-xl border border-[#E8E4DC] p-3">
            <Label htmlFor="setup" className="cursor-pointer">Payment plan set up in Halaxy?</Label>
            <Switch id="setup" checked={f.setup} onCheckedChange={(v) => set({ setup: v })} data-testid="plan-dialog-setup-switch" />
          </div>
          <div className="space-y-1.5">
            <Label>Special arrangements</Label>
            <Textarea rows={2} value={f.special} onChange={(e) => set({ special: e.target.value })} placeholder="Optional" data-testid="plan-dialog-special-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="plan-dialog-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={!canSave || saving} className="bg-[#8B9D83] text-white hover:bg-[#6B7D63]" data-testid="plan-dialog-save-btn">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{mode === 'edit' ? 'Save changes' : 'Save plan'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
